import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { hashtagBeautyFr } from "@okeys/shared-utils"
import { Cross2Icon, PlusIcon, TokensIcon } from "@radix-ui/react-icons"
import React, { useState } from "react"

type EditHashtagsModalProps = {
  isOpen: boolean
  currentHashtags: string[]
  onSave: (newHashtags: string[]) => void
  onClose: () => void
}

export const EditHashtagsModal: React.FC<EditHashtagsModalProps> = ({
  isOpen,
  currentHashtags,
  onSave,
  onClose,
}) => {
  const [hashtags, setHashtags] = useState<string[]>(currentHashtags)
  const [selectedHashtag, setSelectedHashtag] = useState<string>("")

  const handleAddHashtag = () => {
    if (selectedHashtag && !hashtags.includes(selectedHashtag)) {
      setHashtags([...hashtags, selectedHashtag])
      setSelectedHashtag("")
    }
  }

  const handleRemoveHashtag = (hashtagToRemove: string) => {
    setHashtags(hashtags.filter((tag) => tag !== hashtagToRemove))
  }

  const handleSave = () => {
    onSave(hashtags)
  }

  // Filtrer les hashtags disponibles (exclure ceux déjà sélectionnés)
  const availableHashtags = hashtagBeautyFr.filter(
    (tag) => !hashtags.includes(tag)
  )

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <TokensIcon className="h-5 w-5 mr-2 text-blue-500" />
            Modifier les domaines d'activité
          </DialogTitle>
          <DialogDescription>
            Ajoutez ou supprimez les domaines d'activité de votre boutique. Ces
            tags aident les clients à vous trouver.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Hashtags actuels */}
          <div className="space-y-3">
            <Label>Domaines d'activité actuels</Label>
            {hashtags.length > 0 ? (
              <div className="flex flex-wrap gap-2 p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800/50 min-h-[60px]">
                {hashtags.map((tag) => (
                  <div
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-700"
                  >
                    <span className="capitalize">{tag.replace(/_/g, " ")}</span>
                    <button
                      onClick={() => handleRemoveHashtag(tag)}
                      className="ml-1 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors duration-150"
                      aria-label={`Supprimer ${tag}`}
                    >
                      <Cross2Icon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-center">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Aucun domaine d'activité sélectionné
                </p>
              </div>
            )}
          </div>

          {/* Ajouter un nouveau hashtag */}
          <div className="space-y-3">
            <Label htmlFor="hashtagSelect">Ajouter un nouveau domaine</Label>
            <div className="flex gap-2">
              <Select
                value={selectedHashtag}
                onValueChange={setSelectedHashtag}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Sélectionnez un domaine d'activité..." />
                </SelectTrigger>
                <SelectContent>
                  {availableHashtags.length > 0 ? (
                    availableHashtags.map((tag) => (
                      <SelectItem key={tag} value={tag}>
                        <span className="capitalize">
                          {tag.replace(/_/g, " ")}
                        </span>
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="" disabled>
                      Tous les domaines sont déjà sélectionnés
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Button
                type="button"
                onClick={handleAddHashtag}
                disabled={
                  !selectedHashtag || hashtags.includes(selectedHashtag)
                }
                size="sm"
                className="px-3"
              >
                <PlusIcon className="h-4 w-4" />
              </Button>
            </div>
            {availableHashtags.length === 0 && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Vous avez sélectionné tous les domaines d'activité disponibles.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="sm:justify-start pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="button" onClick={handleSave}>
            Sauvegarder les modifications
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
